import { useState } from 'react'
import { Minus, Plus, RotateCcw } from 'lucide-react'
import { ZOOM_DEFAULT, ZOOM_MAX, ZOOM_MIN, ZOOM_STEP, readZoom, setZoom } from '@/lib/ui-zoom'

/**
 * Per-device UI scale control. Shrinks the whole POS so it fits a smaller
 * tablet (e.g. a KDS screen) without horizontal scroll, or enlarges it for a
 * counter monitor read from a few feet back. Runs ZOOM_MIN..ZOOM_MAX
 * (50%–125%) in 5% steps, localStorage-only (see lib/ui-zoom.ts), so it's
 * intentionally NOT in the server-synced theme.
 *
 * Deliberately a −/+ stepper and not a set of presets. The operator's actual
 * task is "scale until the layout fits THIS screen", so they need to land on
 * the exact step that works — often 95% or 85%. Preset buttons can't express
 * those without becoming a wall of options, and rounding the user to the
 * nearest preset either wastes screen space or leaves the overflow they were
 * trying to remove.
 *
 * The centre button shows the current % and resets to the neutral 100% —
 * ZOOM_DEFAULT, *not* ZOOM_MAX, which is a larger number now that the ceiling
 * has been raised. It carries a reset icon only when off 100%: at 100% there
 * is nothing to reset, and the icon that used to sit there permanently
 * (Scaling) reads as "fullscreen", which made the button look like it did
 * something else entirely.
 *
 * Rendered as plain buttons (not menu items) so it can live inside the Radix
 * user-menu dropdown without the dropdown closing on each adjustment.
 */
export function ZoomControl({ showLabel = true }: { showLabel?: boolean }) {
  const [zoom, setZoomState] = useState<number>(() => readZoom())
  const pct = Math.round(zoom * 100)
  const change = (next: number) => setZoomState(setZoom(next))
  const atDefault = zoom === ZOOM_DEFAULT

  return (
    <div className={showLabel ? 'px-2 py-1.5' : ''}>
      {showLabel && (
        <p className="px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Display size
        </p>
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => change(zoom - ZOOM_STEP)}
          disabled={zoom <= ZOOM_MIN}
          aria-label={`Smaller, currently ${pct} percent`}
          title="Smaller"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-md border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          <Minus className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => change(ZOOM_DEFAULT)}
          disabled={atDefault}
          aria-label={atDefault ? `${pct} percent` : `Reset to 100 percent, currently ${pct} percent`}
          title={atDefault ? 'Default size' : 'Reset to 100%'}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-sm font-medium tabular-nums transition-colors hover:bg-muted disabled:hover:bg-transparent"
        >
          {pct}%
          {!atDefault && <RotateCcw className="h-3.5 w-3.5 opacity-60" />}
        </button>
        <button
          type="button"
          onClick={() => change(zoom + ZOOM_STEP)}
          disabled={zoom >= ZOOM_MAX}
          aria-label={`Larger, currently ${pct} percent`}
          title="Larger"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-md border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
